import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { CRMSpacing } from "../../models/CRMSpacing";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { CRMCardOutsideWarning } from "../../components/CRMCardOutsideWarning/CRMCardOutsideWarning";
import { CRMParagraphImportant } from "../../components/Simple/CRMParagraphImportant/CRMParagraphImportant";
import { Padding } from "../../components/BuildingBlocks/Padding";
import CRMCaseMainDetailsCardContainer from "./CRMCaseMainDetailsCardContainer";
import { CRMCaseTimelineCardContainer } from "./CRMCaseTimelineCardContainer";
import CRMCaseDualRepCardContainer from "./CRMCaseDualRepCardContainer";
import { CRMBlockScrollCacheView } from "../../components/CRM/CRMBlock/CRMBlockViews/CRMBlockViews";
import { TBlockContainerStateProps } from "../../state/TBlockContainerStateProps";
import { CRMCardOutsideLabelled } from "../../components/CRMCardOutsideLabelled/CRMCardOutsideLabelled";
import { CRMCaseQuickSummaryCard } from "../../components/CRM/CRMCaseQuickSummaryCard/CRMCaseQuickSummaryCard";
import { CRMCaseGenerateStudyButton } from "../../components/CRM/CRMCaseGenerateStudyButton/CRMCaseGenerateStudyButton";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { FontQuicksand } from "../../components/BuildingBlocks/FontQuicksand";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { Uppercase } from "../../components/Uppercase/Uppercase";
import { Text } from "../../components/BuildingBlocks/Text";
import { CRMCaseChaseBanner } from "../../components/CRM/CRMCaseChaseBanner/CRMCaseChaseBanner";
import { CRMCaseLinkedMatterBanner } from "../../components/CRM/CRMCaseLinkedMatterBanner/CRMCaseLinkedMatterBanner";
import { CRMCaseAuctionPackBanner } from "../../components/Complex/CRMCaseAuctionPackBanner/CRMCaseAuctionPackBanner";
import { CRMCloseChaseContinueCasePopouts } from "../../components/Complex/CRMCloseChaseContinueCasePopouts/CRMCloseChaseContinueCasePopouts";

const CRMCaseDetailsOverviewContainer = (props: TBlockContainerStateProps): JSX.Element => {

    const [chasePopoutIsOpen, setChasePopoutIsOpen] = useState<boolean>(false);

    useEffect(
        () => {
            if (!props.isFirstLoad) {
                props.dispatch({
                    type: "CRM_CASE_OVERVIEW_VIEW",
                    payload: null,
                })
            }
        },
        []
    );

    const isDualRep = () => !! props.state.forms.case_details_page.data.output.details.children.dual_rep.linked_case_form;

    const introducerNotes = () => props.state.forms.case_details_page.data.output.details.children.introducer_notes;
    const introducerCaseRef = () => props.state.forms.case_details_page.data.output.details.children.introducer_external_reference_id;

    const caseDetails = props.state.forms.case_details_page.data.output.details.children;
    const inChaseMode = props.state.forms.chase_mode_form.children.latest_chase.status === "chase";

    const daysInChase = Math.max(
        0,
        Math.floor(
            DateTime.local().diff(
                DateTime.fromISO(props.state.forms.chase_mode_form.children.latest_chase.created_at),
                "days",
            ).days,
        ),
    );

    return (
        <CRMBlockScrollCacheView isShowing={true}>
            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                {/* BANNER - LINKED MATTER (either half of an aborted-transaction pair) */}
                {caseDetails.successor_case_id !== null &&
                    <CRMCaseLinkedMatterBanner
                        direction="successor"
                        linkedCaseId={caseDetails.successor_case_id}
                    />
                }

                {caseDetails.predecessor_case_id !== null &&
                    <CRMCaseLinkedMatterBanner
                        direction="predecessor"
                        linkedCaseId={caseDetails.predecessor_case_id}
                    />
                }

                {/* BANNER - AUCTION PACK (unsent with the auction inside a week) */}
                <CRMCaseAuctionPackBanner
                    caseStatus={caseDetails.status}
                    auctionDate={caseDetails.auction_date}
                    auctionPackSent={caseDetails.auction_pack_sent}
                />

                {/* BANNER - IN CHASE (why the case is paused, zero clicks) */}
                {inChaseMode &&
                    <CRMCaseChaseBanner
                        chaseReasons={props.state.forms.chase_mode_form.children.current_chase_reasons}
                        daysInChase={daysInChase}
                        onReview={() => setChasePopoutIsOpen(true)}
                    />
                }

                {chasePopoutIsOpen &&
                    <CRMCloseChaseContinueCasePopouts
                        state={props.state}
                        dispatch={props.dispatch}
                        isOpen={chasePopoutIsOpen}
                        onClose={() => setChasePopoutIsOpen(false)}
                    />
                }

                <CRMCaseQuickSummaryCard
                    caseId={props.state.forms.case_details_page.data.output.details.children.id}
                />

                {/* Manual case-study run — only meaningful once the case has completed. */}
                {props.state.forms.case_details_page.data.output.details.edited.has_completed &&
                    <CRMCaseGenerateStudyButton
                        caseId={props.state.forms.case_details_page.data.output.details.children.id}
                    />
                }

                {props.state.forms.case_details_page.data.output.sail_homes_case_id &&
                    <CRMCardOutsideWarning
                        labelColour="primary-6"
                        iconColor="neutral-paper"
                        borderRounding="right"
                        icon="sailboat">
                            <Padding spacing={CRMSpacing.MEDIUM}>
                                <CRMParagraphImportant>
                                    Sail Homes Listing!
                                </CRMParagraphImportant>
                            </Padding>
                    </CRMCardOutsideWarning>
                }
                <CRMCaseTimelineCardContainer {...props} />

                <SpacingRow 
                    spacing={CRMSpacing.MEDIUM} 
                    childSize="1fr 1fr"
                    alignItems="flex-start"
                >
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>

                        {/* CARD - MAIN DETAILS */}
                        <CRMCaseMainDetailsCardContainer {...props} />

                        {/* CARD - DUAL REP */}
                        {isDualRep() &&
                            <CRMCaseDualRepCardContainer {...props} />
                        }
                    </SpacingColumn>
                </SpacingRow>

                {/* CARD - INTRODUCER DETAILS */}
                {(introducerCaseRef()  !== "" || introducerNotes()) &&
                    <SpacingRow 
                        spacing={CRMSpacing.MEDIUM} 
                        childSize="1fr"
                    >
                        <CRMCardOutsideLabelled
                            labelColor="highlights-calming-pink-4"
                            shadow={true}
                        >

                        <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                            <FontQuicksand>
                                <Text size={CRMFontSizes.SMALL} lineHeight={1.2}>
                                    <Uppercase>
                                        <WeightBold>
                                            Introducer Details
                                        </WeightBold>
                                    </Uppercase>
                                </Text>
                            </FontQuicksand>
                            
                            <SpacingColumn spacing={CRMSpacing.TINY}>
                                
                                {/* INTRODUCER CASE REF */}
                                {introducerCaseRef() !== "" &&
                                    <CRMParagraph>
                                        <WeightBold>Their case ref: </WeightBold>
                                        {introducerCaseRef()}
                                    </CRMParagraph>
                                }

                                {/* INTRODUCER NOTES */}
                                {introducerNotes() !== "" &&
                                    <CRMParagraph>
                                        <WeightBold>
                                            Their notes on this case:
                                        </WeightBold>
                                        <CRMParagraph>
                                            {introducerNotes()}
                                        </CRMParagraph>
                                    </CRMParagraph>
                                }
                                </SpacingColumn>
                            </SpacingColumn>
                        </CRMCardOutsideLabelled>

                    </SpacingRow>
                }
            </SpacingColumn>
        </CRMBlockScrollCacheView>
    );
}

export default CRMCaseDetailsOverviewContainer;
